import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  adminEliteNetworkList, adminEliteNetworkCreate, adminEliteNetworkUpdate,
  adminEliteNetworkDelete, adminEliteNetworkReorder, adminUploadEliteNetwork,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Crown, Upload, Trash2, ImageIcon, Eye, EyeOff,
  ArrowUp, ArrowDown, Loader2, RefreshCcw,
} from 'lucide-react';

export default function AdminEliteNetworkTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ image_url: '', caption: '' });
  const fileRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminEliteNetworkList();
      setItems(d.items || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function up(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  async function onPickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error('File too large (max 8MB)');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const r = await adminUploadEliteNetwork(f);
      up('image_url', r.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function addEntry() {
    if (!form.image_url) { toast.error('Upload an image first'); return; }
    try {
      await adminEliteNetworkCreate({
        image_url: form.image_url,
        caption: form.caption.trim() || null,
        active: true,
      });
      toast.success('Image added to Elite Network');
      setForm({ image_url: '', caption: '' });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
  }

  async function removeEntry(id) {
    try { await adminEliteNetworkDelete(id); toast.success('Removed'); await refresh(); }
    catch { toast.error('Delete failed'); }
  }

  async function toggleActive(it) {
    try { await adminEliteNetworkUpdate(it.id, { ...it, active: !it.active }); await refresh(); }
    catch { toast.error('Toggle failed'); }
  }

  async function moveUp(idx) {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setItems(next);
    try { await adminEliteNetworkReorder(next.map((x) => x.id)); }
    catch { toast.error('Reorder failed'); refresh(); }
  }
  async function moveDown(idx) {
    if (idx >= items.length - 1) return;
    const next = [...items];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    setItems(next);
    try { await adminEliteNetworkReorder(next.map((x) => x.id)); }
    catch { toast.error('Reorder failed'); refresh(); }
  }

  return (
    <div className="space-y-5" data-testid="admin-elite-network-tab">
      {/* Upload form */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Crown className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Add Elite Network Image
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Upload lifestyle / mentorship / luxury / community photos — they will appear in the
            "Access The Elite Network" marquee on the homepage. As soon as you upload at least one
            image, the default sample images are replaced by yours.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-[200px,1fr]">
            {/* Image preview / picker */}
            <div className="space-y-2">
              <div
                className="aspect-[4/5] rounded-md border border-dashed border-white/10 bg-white/[0.02] overflow-hidden flex items-center justify-center cursor-pointer hover:border-[hsl(43_92%_56%)]/40 transition-colors relative"
                onClick={() => fileRef.current?.click()}
                data-testid="en-upload-zone"
              >
                {uploading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-[hsl(43_92%_56%)]" />
                ) : form.image_url ? (
                  <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-xs text-muted-foreground p-3">
                    <Upload className="h-7 w-7 mx-auto mb-2 opacity-60" />
                    Tap to upload<br />JPG / PNG / HEIC<br />(max 8 MB)
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onPickFile}
                className="hidden"
                data-testid="en-file-input"
              />
              {form.image_url && (
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => up('image_url', '')}
                  className="w-full h-8 border-white/10 bg-white/[0.02] gap-1 text-xs"
                >
                  <Trash2 className="h-3 w-3" /> Clear image
                </Button>
              )}
            </div>

            {/* Meta fields */}
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Caption / Label (optional)
                </Label>
                <Input
                  value={form.caption} onChange={(e) => up('caption', e.target.value)}
                  placeholder='Defaults to "ALVEETRADE NETWORK" chip if empty'
                  className="mt-1 bg-white/[0.02] border-white/10 text-sm"
                  maxLength={200} data-testid="en-caption"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Shown as the small gold chip at the bottom of each image. Leave blank to keep the
                  default "ALVEETRADE NETWORK" chip.
                </p>
              </div>
              <div className="flex items-center justify-end pt-1">
                <Button
                  onClick={addEntry}
                  disabled={!form.image_url || uploading}
                  className="bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_50%)] text-black font-semibold gap-2"
                  data-testid="en-save"
                >
                  <Upload className="h-4 w-4" /> Add to homepage
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="card-surface">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Uploaded Elite Network Images
            {items.length > 0 && <span className="text-xs text-muted-foreground font-normal">· {items.length}</span>}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={refresh} disabled={loading}
            className="h-9 border-white/10 bg-white/[0.02] gap-1">
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-10">
              No images uploaded yet. Upload your first lifestyle / network photo above —
              the homepage will replace the default sample images with your uploads automatically.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((it, idx) => (
                <div key={it.id} className="rounded-md border border-white/10 bg-white/[0.02] overflow-hidden flex flex-col"
                  data-testid={`en-item-${it.id}`}>
                  <div className={`relative bg-black ${!it.active ? 'opacity-40' : ''}`}>
                    <img src={it.image_url} alt="" className="w-full max-h-[260px] object-cover" />
                    {!it.active && (
                      <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-white bg-black/40">Hidden</div>
                    )}
                  </div>
                  <div className="p-3 space-y-2 flex-1 flex flex-col">
                    <div className="text-xs text-[hsl(0_0%_88%)] line-clamp-2 min-h-[2.4em]">
                      {it.caption || <span className="text-muted-foreground italic">Default chip · "ALVEETRADE NETWORK"</span>}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="ml-auto">#{idx + 1}</span>
                    </div>
                    <div className="flex items-center gap-1 pt-1 border-t border-white/5">
                      <Button size="sm" variant="outline" onClick={() => moveUp(idx)} disabled={idx === 0}
                        className="h-7 px-1.5 border-white/10 bg-white/[0.02]" title="Move up">
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => moveDown(idx)} disabled={idx >= items.length - 1}
                        className="h-7 px-1.5 border-white/10 bg-white/[0.02]" title="Move down">
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                      <div className="ml-auto flex items-center gap-2 text-[10px] text-muted-foreground">
                        <Switch checked={!!it.active} onCheckedChange={() => toggleActive(it)} data-testid={`en-toggle-${it.id}`} />
                        {it.active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline"
                            className="h-7 px-1.5 border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.05)] text-[hsl(0_72%_60%)] hover:bg-[rgba(225,29,46,0.12)]"
                            data-testid={`en-del-${it.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove this image?</AlertDialogTitle>
                            <AlertDialogDescription>
                              The image will disappear from the homepage immediately. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => removeEntry(it.id)}
                              className="bg-[hsl(0_72%_50%)] hover:bg-[hsl(0_72%_45%)] text-white">Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
